import {useEffect, useState} from "react";
import {Save, Plus, Trash2, PhoneCall} from "lucide-react";
import {apiGet, apiPost, apiDelete} from "@/lib/api";
import client from "@/lib/api";
import {TRADITIONS} from "@/lib/constants";

const KINDS = [["gfi", "Your Personal Concierge"], ["report", "Purchase Decision Reports"], ["brand", "Find a brand"]];
const STATUSES = ["Call back requested", "Called", "Quote shared", "In progress", "Delivered", "Closed"];

function LeadRow({lead, kind, notify, onSaved}) {
  const [v, setV] = useState({status: lead.status, agreed_amount: lead.agreed_amount || "", payment_status: lead.payment_status || "not_due", admin_note: lead.admin_note || ""});
  const save = async () => {
    try { const r = await client.patch(`/admin/leads/${kind}/${lead.id}`, v).then(x => x.data); onSaved(r); notify("Lead updated"); }
    catch { notify("Could not update lead"); }
  };
  const title = lead.product_name || lead.looking_for || lead.plan_name || lead.plan_id;
  return (
    <div className="lead-row" data-testid={`lead-${lead.id}`}>
      <div className="lead-main">
        <strong>{title}</strong>
        <small>{lead.channel === "whatsapp" && <b className="wa-tag">WhatsApp</b>} {lead.name} · {lead.user_email} · <PhoneCall size={10}/> {lead.phone}{lead.callback_time ? ` · ${lead.callback_time}` : ""}</small>
        <p className="admin-row-desc">{lead.preferences || lead.concerns || lead.category || ""}{lead.locations || lead.ship_to ? ` · Ship to ${lead.locations || lead.ship_to}` : ""}{lead.product_link ? ` · ${lead.product_link}` : ""}</p>
      </div>
      <div className="lead-edit">
        <select data-testid={`lead-status-${lead.id}`} value={v.status} onChange={e => setV({...v, status: e.target.value})}>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <input data-testid={`lead-amount-${lead.id}`} value={v.agreed_amount} onChange={e => setV({...v, agreed_amount: e.target.value})} placeholder="Agreed amount e.g. ₹4,500"/>
        <select data-testid={`lead-payment-${lead.id}`} value={v.payment_status} onChange={e => setV({...v, payment_status: e.target.value})}>
          <option value="not_due">No payment yet</option><option value="due">Payment due</option><option value="paid">Marked paid</option>
        </select>
        <input data-testid={`lead-note-${lead.id}`} value={v.admin_note} onChange={e => setV({...v, admin_note: e.target.value})} placeholder="Internal note"/>
        <button data-testid={`lead-save-${lead.id}`} className="coral-button sm" onClick={save}><Save size={12}/> Save</button>
      </div>
    </div>
  );
}

export function LeadsPanel({notify}) {
  const [kind, setKind] = useState("gfi");
  const [leads, setLeads] = useState([]);
  useEffect(() => { apiGet(`/admin/leads/${kind}`).then(setLeads).catch(() => setLeads([])); }, [kind]);
  return (
    <section className="admin-panel wide" data-testid="admin-leads">
      <div className="admin-panel-head">
        <div><div className="eyebrow">CALL-BACK INBOX</div><h2>Leads ({leads.length})</h2></div>
        <select data-testid="leads-kind" value={kind} onChange={e => setKind(e.target.value)}>
          {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <p className="admin-help">Call the member, agree a discounted price, record it here and mark it paid once received. Members see the status and amount in their profile. Online payment is switched off for now.</p>
      {leads.length === 0 ? <p className="admin-help">No leads yet.</p> : leads.map(l => (
        <LeadRow key={l.id} lead={l} kind={kind} notify={notify} onSaved={(r) => setLeads(ls => ls.map(x => x.id === r.id ? r : x))}/>
      ))}
    </section>
  );
}

export function PlansPanel({notify}) {
  const [plans, setPlans] = useState([]);
  useEffect(() => { apiGet("/concierge/plans").then(setPlans).catch(() => {}); }, []);
  const edit = (i, k, val) => { const c = [...plans]; c[i] = {...c[i], [k]: val}; setPlans(c); };
  const save = async () => {
    try { await client.put("/concierge/plans", {plans}); notify("Your Personal Concierge plans saved"); }
    catch { notify("Admin access required to update plans"); }
  };
  return (
    <section className="admin-panel wide" data-testid="admin-plans">
      <div className="admin-panel-head">
        <div><div className="eyebrow">YOUR PERSONAL CONCIERGE</div><h2>Plans (no public pricing)</h2></div>
        <button data-testid="save-plans" className="coral-button sm" onClick={save}><Save size={13}/> Save</button>
      </div>
      <div className="plan-editor">
        {plans.map((p, i) => (
          <div className="plan-edit-row" key={p.plan_id}>
            <input data-testid={`plan-name-${p.plan_id}`} value={p.name} onChange={e => edit(i, "name", e.target.value)}/>
            <input data-testid={`plan-cadence-${p.plan_id}`} value={p.cadence} onChange={e => edit(i, "cadence", e.target.value)}/>
            <input data-testid={`plan-blurb-${p.plan_id}`} value={p.blurb} onChange={e => edit(i, "blurb", e.target.value)}/>
          </div>
        ))}
      </div>
    </section>
  );
}

const EMPTY = {name: "", city: "", headline: "", bio: "", tradition: "Vaishnava"};

function ProfileForm({initial, onSubmit, submitLabel, testId}) {
  const [v, setV] = useState(initial);
  const ch = e => setV({...v, [e.target.name]: e.target.value});
  return (
    <form className="profile-form" data-testid={testId} onSubmit={e => { e.preventDefault(); onSubmit(v, () => setV(EMPTY)); }}>
      <input name="name" required minLength={2} value={v.name} onChange={ch} placeholder="Name" data-testid={`${testId}-name`}/>
      <input name="city" value={v.city} onChange={ch} placeholder="City, country" data-testid={`${testId}-city`}/>
      <input name="headline" value={v.headline} onChange={ch} placeholder="Headline" data-testid={`${testId}-headline`}/>
      <select name="tradition" value={v.tradition} onChange={ch} data-testid={`${testId}-tradition`}>{TRADITIONS.map(t => <option key={t}>{t}</option>)}</select>
      <textarea name="bio" value={v.bio} onChange={ch} placeholder="Short bio" data-testid={`${testId}-bio`}/>
      <button className="coral-button sm" type="submit" data-testid={`${testId}-submit`}>{submitLabel}</button>
    </form>
  );
}

export function ProfilesPanel({notify}) {
  const [profiles, setProfiles] = useState([]);
  const [editing, setEditing] = useState(null);
  useEffect(() => { apiGet("/admin/profiles").then(setProfiles).catch(() => setProfiles([])); }, []);
  const create = async (v, reset) => {
    try { const r = await apiPost("/admin/profiles", v); setProfiles(p => [...p, r]); reset(); notify("Demo profile added"); }
    catch { notify("Could not add profile"); }
  };
  const update = async (v) => {
    try { const r = await client.patch(`/admin/profiles/${editing}`, {name: v.name, city: v.city || "", headline: v.headline || "", bio: v.bio || "", tradition: v.tradition || "Vaishnava"}).then(x => x.data); setProfiles(p => p.map(x => x.user_id === r.user_id ? r : x)); setEditing(null); notify("Profile updated"); }
    catch { notify("Could not update profile"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this demo profile and all of its questions, answers and posts?")) return;
    try { await apiDelete(`/admin/profiles/${id}`); setProfiles(p => p.filter(x => x.user_id !== id)); notify("Demo profile deleted"); }
    catch { notify("Could not delete"); }
  };
  return (
    <section className="admin-panel wide" data-testid="admin-profiles">
      <div className="admin-panel-head"><div><div className="eyebrow">DEMO PROFILES</div><h2>Sample members ({profiles.length})</h2></div></div>
      <p className="admin-help">These sample Gujarati profiles power the seeded samaj, questions and answers. Edit names or bios, add new ones, or delete them (their posts go too).</p>
      {profiles.map(p => editing === p.user_id ? (
        <ProfileForm key={p.user_id} initial={{...EMPTY, ...p}} onSubmit={update} submitLabel="Save" testId={`profile-edit-${p.user_id}`}/>
      ) : (
        <div key={p.user_id} className="admin-row" data-testid={`demo-profile-${p.user_id}`}>
          <div><strong>{p.name}</strong><small>{p.headline} · {p.city} · {p.tradition}</small></div>
          <button data-testid={`demo-edit-${p.user_id}`} className="outline-button sm" onClick={() => setEditing(p.user_id)}>Edit</button>
          <button data-testid={`demo-delete-${p.user_id}`} className="outline-button sm" onClick={() => remove(p.user_id)}><Trash2 size={13}/></button>
        </div>
      ))}
      <div className="eyebrow" style={{marginTop: 18}}><Plus size={11}/> ADD DEMO PROFILE</div>
      <ProfileForm initial={EMPTY} onSubmit={create} submitLabel="Add profile" testId="profile-create"/>
    </section>
  );
}

export function EventApprovalsPanel({notify}) {
  const [events, setEvents] = useState([]);
  useEffect(() => { apiGet("/admin/events/pending").then(setEvents).catch(() => setEvents([])); }, []);
  const decide = async (id, action) => {
    try { await apiPost(`/admin/events/${id}/${action}`, {}); setEvents(e => e.filter(x => x.event_id !== id)); notify(action === "approve" ? "Event is live in Events near me" : "Event rejected"); }
    catch { notify("Could not update event"); }
  };
  return (
    <section className="admin-panel wide" data-testid="admin-event-approvals">
      <div className="admin-panel-head"><div><div className="eyebrow">PUBLIC EVENTS</div><h2>Awaiting approval ({events.length})</h2></div></div>
      {events.length === 0 ? <p className="admin-help">No public events waiting. Member-listed garba nights and melas appear here first.</p> : events.map(ev => (
        <div key={ev.event_id} className="admin-row" data-testid={`pending-event-${ev.event_id}`}>
          <div><strong>{ev.title}</strong><small>{ev.category} · {ev.city} · {new Date(ev.when).toLocaleString()} · by {ev.host_name}</small><p className="admin-row-desc">{ev.location} {ev.description}</p></div>
          <button data-testid={`approve-event-${ev.event_id}`} className="coral-button sm" onClick={() => decide(ev.event_id, "approve")}>Approve</button>
          <button data-testid={`reject-event-${ev.event_id}`} className="outline-button sm" onClick={() => decide(ev.event_id, "reject")}>Reject</button>
        </div>
      ))}
    </section>
  );
}

export function ContactPanel({notify}) {
  const [num, setNum] = useState("");
  const [saved, setSaved] = useState("");
  useEffect(() => { apiGet("/settings/public").then(s => { setNum(s.whatsapp_number); setSaved(s.whatsapp_number); }).catch(() => {}); }, []);
  const save = async () => {
    try { const r = await client.put("/admin/settings/contact", {whatsapp_number: num}).then(x => x.data); setSaved(r.whatsapp_number); setNum(r.whatsapp_number); notify("WhatsApp number saved"); }
    catch (e) { notify(e?.response?.data?.detail || "Could not save number"); }
  };
  return (
    <section className="admin-panel" data-testid="admin-contact">
      <div className="admin-panel-head"><div><div className="eyebrow">WHATSAPP CALL-BACKS</div><h2>India desk number</h2></div></div>
      <p className="admin-help">{saved ? `Members' "WhatsApp us" opens a chat with +${saved}.` : "Not set yet — members' WhatsApp requests are saved and they're told you'll call back."}</p>
      <div className="contact-row">
        <input data-testid="whatsapp-number-input" value={num} onChange={e => setNum(e.target.value)} placeholder="+91 98xxx xxxxx"/>
        <button data-testid="whatsapp-number-save" className="coral-button sm" onClick={save}><Save size={13}/> Save</button>
      </div>
    </section>
  );
}
